import { ObjectIdAsHexString } from "kreate-common/modules/business-types";
import { getResourceKey$GetGalleryAlbum } from "kreate-server/commands/GetGalleryAlbum/fetcher";
import { handler$GetGalleryAlbum } from "kreate-server/commands/GetGalleryAlbum/handler";
import { GetGalleryAlbum$Result } from "kreate-server/commands/GetGalleryAlbum/typing";
import { getResourceKey$GetGalleryAlbumList } from "kreate-server/commands/GetGalleryAlbumList/fetcher";
import { handler$GetGalleryAlbumList } from "kreate-server/commands/GetGalleryAlbumList/handler";
import { GetGalleryAlbumList$Result } from "kreate-server/commands/GetGalleryAlbumList/typing";
import { getResourceKey$GetGalleryCollector } from "kreate-server/commands/GetGalleryCollector/fetcher";
import { handler$GetGalleryCollector } from "kreate-server/commands/GetGalleryCollector/handler";
import { GetGalleryCollector$Result } from "kreate-server/commands/GetGalleryCollector/typing";
import { getResourceKey$LoginStatus } from "kreate-server/commands/LoginStatus/fetcher";
import { handler$LoginStatus } from "kreate-server/commands/LoginStatus/handler";
import { LoginStatus$Result } from "kreate-server/commands/LoginStatus/typing";
import { GetServerSideProps } from "next";
import { SWRConfig, unstable_serialize } from "swr";
import { z } from "zod";

import PageGalleryCollector$Album from "../../../../containers/PageGalleryCollector$Album";
import { formatFallback, omitUndefinedRecursively } from "../../../../utils";

import { intentionallyIgnoreError } from "@/modules/common-utils/assert";
import { getDb } from "@/modules/mongodb";

const PageQuery = z.object({
  addressIsh: z.string(),
  albumIsh: z.string(),
});

type Props = {
  userId: ObjectIdAsHexString;
  pageProps: React.ComponentProps<typeof PageGalleryCollector$Album>;
  fallback: {
    LoginStatus: LoginStatus$Result | undefined;
    GetGalleryCollector: GetGalleryCollector$Result | undefined;
    GetGalleryAlbumList: GetGalleryAlbumList$Result | undefined;
    GetGalleryAlbum: GetGalleryAlbum$Result | undefined;
  };
};

export default function Route({ userId, pageProps, fallback }: Props) {
  return (
    <SWRConfig
      value={{
        fallback: formatFallback({
          [unstable_serialize(getResourceKey$LoginStatus())]:
            fallback.LoginStatus,
          [unstable_serialize(
            getResourceKey$GetGalleryCollector({
              addressOrHandle: pageProps.addressIsh,
            })
          )]: fallback.GetGalleryCollector,
          [unstable_serialize(
            getResourceKey$GetGalleryAlbumList({ createdBy: userId })
          )]: fallback.GetGalleryAlbumList,
          [unstable_serialize(
            getResourceKey$GetGalleryAlbum({
              galleryAlbumId: pageProps.galleryAlbumId,
            })
          )]: fallback.GetGalleryAlbum,
        }),
      }}
    >
      <PageGalleryCollector$Album {...pageProps} />
    </SWRConfig>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const pageQuerySP = PageQuery.safeParse(context.query);
  if (!pageQuerySP.success) return { notFound: true };
  const { addressIsh, albumIsh } = pageQuerySP.data;

  const db = await getDb();
  const [fallback$LoginStatus, fallback$GetGalleryCollector] =
    await Promise.all([
      handler$LoginStatus(db, {
        token: context.req.cookies["token"],
      }).catch(intentionallyIgnoreError),
      handler$GetGalleryCollector(db, {
        addressOrHandle: addressIsh,
      }).catch(intentionallyIgnoreError),
    ]);

  const albumIdSP = ObjectIdAsHexString.safeParse(albumIsh);
  const userProfile = fallback$GetGalleryCollector?.populated?.userProfile;
  if (!userProfile) return { notFound: true };

  const [fallback$GetGalleryAlbum, fallback$GetGalleryAlbumList] =
    await Promise.all([
      handler$GetGalleryAlbum(
        db,
        albumIdSP.success
          ? { galleryAlbumId: albumIdSP.data }
          : { userId: userProfile.userId, albumHandle: albumIsh }
      ).catch(intentionallyIgnoreError),
      handler$GetGalleryAlbumList(db, { createdBy: userProfile.userId }).catch(
        intentionallyIgnoreError
      ),
    ]);

  if (!fallback$GetGalleryAlbum) return { notFound: true };

  const isGetGalleryAlbumListTooBig =
    fallback$GetGalleryAlbumList?.galleryAlbums.length &&
    fallback$GetGalleryAlbumList?.galleryAlbums.length > 64;

  return {
    props: omitUndefinedRecursively<Props>({
      userId: userProfile.userId,
      pageProps: {
        addressIsh,
        galleryAlbumId: fallback$GetGalleryAlbum.galleryAlbumId,
      },
      fallback: {
        LoginStatus: fallback$LoginStatus,
        GetGalleryCollector: fallback$GetGalleryCollector,
        GetGalleryAlbumList: isGetGalleryAlbumListTooBig
          ? undefined
          : fallback$GetGalleryAlbumList,
        GetGalleryAlbum: fallback$GetGalleryAlbum,
      },
    }),
  };
};
